<template>
  <teleport to="body">
    <transition name="h-modal-fade">
      <div v-if="modelValue" class="h-modal-scrim" @click.self="onScrim">
        <div
          ref="panel"
          class="h-modal"
          role="alertdialog"
          aria-modal="true"
          :aria-label="title"
          @keydown.esc.prevent="onEsc"
          @keydown.tab="onTab"
        >
          <div class="h-modal__head">
            <div :class="['h-modal__icon', danger ? 'h-modal__icon--danger' : 'h-modal__icon--warning']">
              <svg viewBox="0 0 24 24"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4M12 17h.01"/></svg>
            </div>
            <div class="h-modal__headtext">
              <h3 class="h-modal__title">{{ title }}</h3>
              <p class="h-modal__desc"><slot name="description">{{ description }}</slot></p>
            </div>
            <button type="button" class="h-modal__x" aria-label="Close" @click="cancel">✕</button>
          </div>

          <div class="h-modal__body">
            <div class="h-confirm-box">
              Type <span class="h-mono">{{ confirmWord }}</span> to confirm — this cannot be undone.
            </div>
            <input
              ref="input"
              v-model="typed"
              class="h-confirm-input"
              type="text"
              :placeholder="confirmWord"
              autocomplete="off"
              autocapitalize="off"
              spellcheck="false"
              @keydown.enter="tryConfirm"
            />
          </div>

          <div class="h-modal__foot">
            <button type="button" class="h-btn h-btn--secondary" @click="cancel">{{ cancelLabel }}</button>
            <button
              type="button"
              class="h-btn h-btn--danger"
              :disabled="!matches"
              @click="tryConfirm"
            >
              {{ confirmLabel }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
// Destructive confirm — requires the operator to type an exact confirmation
// word before the danger button enables. Scrim + level-3 shadow + focus trap.
// Esc / scrim / Cancel all abort; only the explicit danger control commits
// (design/components/modals-overlays.html rule for destructive dialogs).
export default {
  name: 'HermesConfirmModal',
  props: {
    modelValue: { type: Boolean, default: false },
    title: { type: String, default: 'Confirm' },
    description: { type: String, default: '' },
    // The word the operator must type verbatim, e.g. 'CANCEL' / 'REVOKE'.
    confirmWord: { type: String, default: 'CONFIRM' },
    confirmLabel: { type: String, default: 'Confirm' },
    cancelLabel: { type: String, default: 'Cancel' },
    danger: { type: Boolean, default: true },
    // Case-insensitive match. Default requires exact case (safer for destructive).
    caseSensitive: { type: Boolean, default: true },
  },
  emits: ['update:modelValue', 'confirm', 'cancel'],
  data() {
    return { typed: '', lastFocused: null };
  },
  computed: {
    matches() {
      const a = this.typed.trim();
      const b = this.confirmWord.trim();
      return this.caseSensitive ? a === b : a.toLowerCase() === b.toLowerCase();
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.onOpen();
      else this.onClose();
    },
  },
  methods: {
    onOpen() {
      this.typed = '';
      this.lastFocused = document.activeElement;
      document.documentElement.style.overflow = 'hidden';
      this.$nextTick(() => {
        if (this.$refs.input) this.$refs.input.focus();
      });
    },
    onClose() {
      document.documentElement.style.overflow = '';
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') this.lastFocused.focus();
    },
    cancel() {
      this.$emit('update:modelValue', false);
      this.$emit('cancel');
    },
    onScrim() { this.cancel(); },
    onEsc() { this.cancel(); },
    tryConfirm() {
      if (!this.matches) return;
      this.$emit('confirm');
      this.$emit('update:modelValue', false);
    },
    onTab(e) {
      const f = Array.from(
        this.$refs.panel.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null);
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    },
  },
  beforeUnmount() {
    document.documentElement.style.overflow = '';
  },
};
</script>

<style scoped>
.h-modal-scrim {
  position: fixed; inset: 0; z-index: 1300;
  background: var(--h-bg-overlay, rgba(15, 23, 42, 0.55));
  display: flex; align-items: center; justify-content: center; padding: 24px;
}
.h-modal {
  width: 100%; max-width: 420px;
  background: var(--h-bg-card, #fff);
  border-radius: var(--h-radius-lg, 10px);
  box-shadow: var(--h-shadow-pop);
  border: 1px solid var(--h-border, #E2E8F0);
  overflow: hidden;
  font-family: var(--h-font-body, system-ui);
}
.h-modal__head { padding: 20px 22px 0; display: flex; gap: 14px; align-items: flex-start; }
.h-modal__icon { flex: none; width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; }
.h-modal__icon svg { width: 20px; height: 20px; stroke: currentColor; fill: none; stroke-width: 2; }
.h-modal__icon--danger { background: var(--h-danger-bg, #FEF2F2); color: var(--h-danger-text, #B91C1C); }
.h-modal__icon--warning { background: var(--h-warning-bg, #FFFBEB); color: var(--h-warning-text, #B45309); }
.h-modal__headtext { flex: 1; }
.h-modal__title { font-size: 16px; font-weight: 600; margin: 0 0 4px; color: var(--h-text-primary, #0F172A); }
.h-modal__desc { font-size: 13.5px; color: var(--h-text-secondary, #64748B); margin: 0; }
.h-modal__x { margin-left: auto; border: none; background: none; color: var(--h-text-tertiary, #94A3B8); cursor: pointer; font-size: 18px; line-height: 1; padding: 2px; }
.h-modal__x:hover { color: var(--h-text-primary, #0F172A); }
.h-modal__body { padding: 14px 22px 4px; }
.h-confirm-box { background: var(--h-danger-bg, #FEF2F2); border: 1px solid var(--h-danger-border, #FECACA); border-radius: var(--h-radius, 6px); padding: 11px 13px; font-size: 12.5px; color: var(--h-danger-text, #B91C1C); }
.h-mono { font-family: var(--h-font-mono, monospace); background: rgba(0, 0, 0, 0.06); padding: 1px 5px; border-radius: 4px; }
.h-confirm-input {
  width: 100%; margin-top: 10px; font: inherit; font-size: 14px; padding: 9px 12px;
  border: 1px solid var(--h-border-strong, #CBD5E1); border-radius: var(--h-radius, 6px);
  background: var(--h-bg-card, #fff); color: var(--h-text-primary, #0F172A);
}
.h-confirm-input:focus { outline: none; border-color: var(--h-danger, #EF4444); box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15); }
.h-modal__foot { display: flex; justify-content: flex-end; gap: 10px; padding: 18px 22px 20px; }

.h-btn { font: inherit; font-size: 13.5px; font-weight: 500; padding: 9px 15px; border-radius: var(--h-radius, 6px); border: 1px solid transparent; cursor: pointer; }
.h-btn:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
.h-btn--secondary { background: var(--h-bg-card, #fff); color: var(--h-text-primary, #0F172A); border-color: var(--h-border-strong, #CBD5E1); }
.h-btn--secondary:hover { background: var(--h-bg-subtle, #F1F5F9); }
.h-btn--danger { background: var(--h-danger, #EF4444); color: #fff; }
.h-btn--danger:hover:not(:disabled) { background: var(--h-danger-hover, #DC2626); }
.h-btn--danger:disabled { opacity: 0.5; cursor: not-allowed; }

.h-modal-fade-enter-active, .h-modal-fade-leave-active { transition: opacity 0.16s ease; }
.h-modal-fade-enter-from, .h-modal-fade-leave-to { opacity: 0; }
</style>
